<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  Button, Checkbox, CheckboxField, CheckboxGroup, Description, Field, InlineAlert,
  Label, Radio, RadioField, RadioGroup, StateCard,
} from 'catalyst-vue'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { assignCategories, type CategoryAssignMode } from '../../lib/category-service'
import { assignCollectionCategories } from '../../lib/collection-bulk'
import { type BulkOutcome } from '../../lib/bulk-progress'
import LibraryOverlay from './library-overlay.vue'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: []; done: [outcome: BulkOutcome, mode: CategoryAssignMode] }>()
const cloud = useCloudLibraryStore()
const store = useLibraryEntriesStore()

// 分類統一在雲端目錄，整份書庫是同一份清單。
const categories = computed(() => cloud.categories)
const selectedIds = computed(() => [...store.selection])
const comicCount = computed(() => store.selectedEntries.length)
const collectionCount = computed(() => store.selectedCollections.length)
/** 說明逐字對應行為：集合的分類指派給集合自己，不會動到它的成員。 */
const scopeText = computed(() => {
  if (!collectionCount.value) return `套用到已選取的 ${comicCount.value} 本作品。`
  if (!comicCount.value) return `套用到已選取的 ${collectionCount.value} 個集合本身，集合成員各自的分類不會被動到。`
  return `套用到已選取的 ${comicCount.value} 本作品與 ${collectionCount.value} 個集合；集合的分類指派給集合自己，成員各自的分類不會被動到。`
})
// 只有加入與移除，沒有「設為」——覆蓋會清掉每本作品既有的其他分類指派。
const mode = ref<CategoryAssignMode>('add')
const chosen = ref<string[]>([])
const busy = ref(false)
const error = ref('')

watch(() => props.open, isOpen => { if (isOpen) { mode.value = 'add'; chosen.value = []; error.value = '' } })

function toggle(id: string, checked: boolean) {
  chosen.value = checked ? [...new Set([...chosen.value, id])] : chosen.value.filter(value => value !== id)
}

async function apply() {
  if (!chosen.value.length || !selectedIds.value.length || busy.value) return
  busy.value = true
  error.value = ''
  try {
    // 兩個範圍走同一條路：分類的真相在雲端目錄，本地只是鏡像。
    const comics = await assignCategories(store.selectedComics, chosen.value, mode.value)
    // 集合那一段是另一條路：指派給集合自己，成員不動。任一段失敗都要講出來，成功的那一段不回復。
    const collections = comics.reason
      ? { completed: 0, completedIds: [] as string[], reason: '' }
      : await assignCollectionCategories(store.selectedCollections, chosen.value, mode.value)
    await cloud.refresh(true)
    const outcome = {
      completed: comics.completed + collections.completed,
      completedIds: [...comics.completedIds, ...collections.completedIds],
      reason: comics.reason || collections.reason,
      cause: comics.cause ?? collections.cause,
    }
    emit('done', outcome, mode.value)
    if (!outcome.reason) emit('close')
    else error.value = outcome.reason
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '分類尚未套用'
  } finally { busy.value = false }
}
</script>

<template>
  <LibraryOverlay
    :open="open"
    title="批次分類"
    :description="`${scopeText} 既有的其他分類指派不會被清掉。`"
    :pending="busy"
    @close="emit('close')"
  >
    <InlineAlert v-if="error" tone="error" class="mb-3">{{ error }}</InlineAlert>
    <Field>
      <Label>要做什麼</Label>
      <RadioGroup v-model="mode">
        <RadioField>
          <Radio value="add" color="primary" />
          <Label>加入這些分類</Label>
          <Description>選取的作品都會屬於下面勾選的分類。</Description>
        </RadioField>
        <RadioField>
          <Radio value="remove" color="primary" />
          <Label>移除這些分類</Label>
          <Description>只移除下面勾選的分類，其他指派不動。</Description>
        </RadioField>
      </RadioGroup>
    </Field>
    <CheckboxGroup v-if="categories.length" class="mt-4">
      <CheckboxField v-for="category in categories" :key="category.id">
        <Checkbox
          color="primary"
          :aria-label="category.name"
          :model-value="chosen.includes(category.id)"
          @update:model-value="toggle(category.id, $event)"
        />
        <Label>{{ category.name }}</Label>
      </CheckboxField>
    </CheckboxGroup>
    <StateCard
      v-else
      class="mt-4"
      title="尚無分類"
      message="先到分類管理頁新增一個分類，才能批次指派。"
      :actionable="false"
    />
    <template #actions>
      <Button outline :disabled="busy" @click="emit('close')">取消</Button>
      <Button color="primary" :disabled="busy || !chosen.length || !selectedIds.length" @click="apply">
        {{ busy ? '正在套用…' : `${mode === 'add' ? '加入' : '移除'}分類（${selectedIds.length} 項）` }}
      </Button>
    </template>
  </LibraryOverlay>
</template>
